import { useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material';
import {
  useAdminAdicionarMunicipioAtendidoMutation,
  useAdminListarMunicipiosAtendidosQuery,
  useAdminRemoverMunicipioAtendidoMutation,
  useLazyAdminBuscarMunicipiosQuery,
  type MunicipioMaster,
} from '../../../api';

interface MultiSelectMunicipiosProps {
  idConfig: number;
}

interface OpcaoMunicipio {
  id: number;
  label: string;
  nome: string;
  uf: string;
}

export function MultiSelectMunicipios({ idConfig }: MultiSelectMunicipiosProps): JSX.Element {
  const { data: atendidos, isLoading: carregandoAtendidos } =
    useAdminListarMunicipiosAtendidosQuery(idConfig);
  const [buscarMunicipios, statusBusca] = useLazyAdminBuscarMunicipiosQuery();
  const [adicionar] = useAdminAdicionarMunicipioAtendidoMutation();
  const [remover] = useAdminRemoverMunicipioAtendidoMutation();

  const [opcoes, setOpcoes] = useState<OpcaoMunicipio[]>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const value = useMemo<OpcaoMunicipio[]>(() => {
    if (!atendidos) return [];
    return atendidos.map((m) => ({
      id: m.idMunicipio,
      label: `${m.nome} (${m.uf})`,
      nome: m.nome,
      uf: m.uf,
    }));
  }, [atendidos]);

  function onInputChange(_e: unknown, valor: string): void {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const termo = valor.trim();
    if (termo.length < 2) {
      setOpcoes([]);
      return;
    }
    debounceRef.current = setTimeout(() => {
      buscarMunicipios({ busca: termo, limite: 20 })
        .unwrap()
        .then((resultado: MunicipioMaster[]) => {
          setOpcoes(
            resultado.map((m) => ({
              id: m.id,
              label: `${m.nome} (${m.uf})`,
              nome: m.nome,
              uf: m.uf,
            })),
          );
        })
        .catch((err) => {
          console.warn('Falha ao buscar municípios', err);
          setOpcoes([]);
        });
    }, 300);
  }

  async function onChange(_e: unknown, novos: OpcaoMunicipio[]): Promise<void> {
    const idsAntes = new Set(value.map((v) => v.id));
    const idsDepois = new Set(novos.map((v) => v.id));

    const adicionados = novos.filter((v) => !idsAntes.has(v.id));
    const removidos = value.filter((v) => !idsDepois.has(v.id));

    for (const item of adicionados) {
      try {
        await adicionar({ idConfiguracaoCardapio: idConfig, idMunicipio: item.id }).unwrap();
      } catch (err) {
        console.warn('Falha ao adicionar município', err);
      }
    }
    for (const item of removidos) {
      try {
        await remover({ idConfiguracaoCardapio: idConfig, idMunicipio: item.id }).unwrap();
      } catch (err) {
        console.warn('Falha ao remover município', err);
      }
    }
  }

  const carregando = carregandoAtendidos || statusBusca.isFetching;

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Municípios atendidos
      </Typography>
      <Autocomplete
        multiple
        value={value}
        options={opcoes}
        onChange={onChange}
        onInputChange={onInputChange}
        getOptionLabel={(opcao) => `${opcao.nome} (${opcao.uf})`}
        isOptionEqualToValue={(opcao, val) => opcao.id === val.id}
        filterOptions={(x) => x}
        loading={carregando}
        noOptionsText="Digite ao menos 2 caracteres"
        renderOption={(props, opcao) => (
          <li {...props} key={opcao.id}>
            <Box>
              <Typography variant="body2">{opcao.nome}</Typography>
              <Typography variant="caption" color="text.secondary">
                {opcao.uf}
              </Typography>
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Digite o nome do município"
            helperText="Defina quais cidades sua loja entrega. Deixe vazio para aceitar qualquer município."
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {carregando ? <CircularProgress color="inherit" size={18} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Box>
  );
}
